import { defineStore } from "pinia";
import type { MetadataItem } from "~~/types/metadata";
import type { ArtType, ArtworkField } from "~~/types/artwork";
import { useMessage } from "./app-toast";

interface MetaStoreState {
    saving: boolean;
    updating: boolean;
    deleting: boolean;
    fetching: boolean;
    showEdit: boolean;
    showDelete: boolean;
    showNew: boolean;
    edited: MetadataItem | null;
    meta: MetadataItem[];
    field: ArtworkField;
    artType: ArtType;
    search: string;
    sortBy: "en" | "ru" | "hy";
    page: number;
    limit: number;
    hasMore: boolean;
}

export const useMetaStore = defineStore("MetaStore", {
    state: (): MetaStoreState => ({
        saving: false,
        updating: false,
        deleting: false,
        fetching: false,
        showEdit: false,
        showDelete: false,
        showNew: false,
        edited: null,
        meta: [],
        field: "",
        artType: "painting",
        search: "",
        sortBy: "hy",
        page: 1,
        limit: 200,
        hasMore: true,
    }),
    actions: {
        openNew() {
            this.showNew = true;
        },
        closeNew() {
            this.showNew = false;
        },
        openEdit(item: MetadataItem) {
            if (!item) return;

            this.edited = {
                ...item,
                label: {
                    hy: item.label?.hy ?? "",
                    en: item.label?.en ?? "",
                    ru: item.label?.ru ?? "",
                },
                allowedArtTypes: item.allowedArtTypes ? [...item.allowedArtTypes] : [],
            };

            this.showEdit = true;
        },
        closeEdit() {
            this.showEdit = false;
            this.edited = null;
        },
        openDelete() {
            this.showDelete = true;
        },
        closeDelete() {
            this.showDelete = false;
        },

        async setArtType(t: ArtType) {
            if (!t) return;
            this.artType = t;
            await this.getMeta();
        },

        async getMeta(isAppend = false) {
            const { $api } = useNuxtApp()
            const toast = useMessage()
            try {
                this.fetching = true;

                // Construct query parameters from state
                const params = {
                    field: this.field,
                    artType: this.artType,
                    search: this.search,
                    sortBy: this.sortBy,
                    page: this.page,
                    limit: this.limit,
                };

                const data = await $api<any>("/meta", { params });

                if (data.success) {
                    // If appending (infinite scroll), add to list; otherwise, replace
                    this.meta = isAppend ? [...this.meta, ...data.data] : data.data;

                    // Update the "hasMore" flag from the server response
                    this.hasMore = data.pagination?.hasMore ?? false;
                }
            } catch (e) {
                toast.error(e)
            } finally {
                this.fetching = false;
            }
        },

        async loadMore() {
            if (this.hasMore && !this.fetching) {
                this.page++;
                await this.getMeta(true);
            }
        }
    },
});
